import { DreamTopic, DREAMS_DATABASE, DREAM_CATEGORIES, ARABIC_LETTERS } from '../data/dreamsData';
import { DICTIONARY_DB, EDITORIAL_RESOLUTION, findDictionaryBySlug } from '../data/dictionaryDb';

export const SITE_CONFIG = {
  baseUrl: 'https://ibn-sirin.mshru3.com',
  siteName: 'ابن سيرين | مفسر الأحلام الذكي وقاموس الرؤى',
  defaultDescription: 'تطبيق وموسوعة ابن سيرين لتفسير الأحلام والرؤى بالذكاء الاصطناعي مستندة إلى مصادر تراثية. فسر حلمك فوراً مجاناً وابحث في قاموس الرموز المفهرس بالحروف والأقسام.',
  ogImage: 'https://ardalel.neocities.org/ImgTool/dream_ImgTool.jpg',
  twitterHandle: '@IbnSirinApp',
  inLanguage: 'ar',
};

export interface PageSeoMetadata {
  title: string;
  description: string;
  canonicalUrl: string;
  keywords: string[];
  ogType: 'website' | 'article';
  ogImage: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  publishedTime?: string;
  modifiedTime?: string;
  jsonLdSchemas: object[];
  noindex?: boolean;
  robots?: string;
}

export function cleanCanonicalUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (normalizedPath === '/') {
    return SITE_CONFIG.baseUrl;
  }
  return `${SITE_CONFIG.baseUrl}${normalizedPath.replace(/\/+$/, '')}`;
}

export const GOOGLE_PLAY_APP_URL = 'https://play.google.com/store/apps/details?id=com.ahmedaher.ibnsirin';

export const APP_SCREENSHOTS: { file: string; width: number; height: number; name: string; caption: string }[] = [
  {
    file: 'Screenshot (1).webp',
    width: 244,
    height: 128,
    name: 'واجهة تفسير الحلم بالذكاء الاصطناعي في تطبيق تفسير الأحلام لابن سيرين و AI للأندرويد',
    caption: 'شاشة تفسير الحلم بالذكاء الاصطناعي'
  },
  {
    file: 'Screenshot (2).webp',
    width: 244,
    height: 128,
    name: 'إدخال نص الحلم وبيانات الرائي والإدخال الصوتي في تطبيق ابن سيرين للأندرويد',
    caption: 'كتابة الحلم أو الإملاء الصوتي مع بيانات الرائي'
  },
  {
    file: 'Screenshot (3).webp',
    width: 244,
    height: 128,
    name: 'جلسة التفسير بالذكاء الاصطناعي مع اختيار المفسر والمنهج في تطبيق ابن سيرين للأندرويد',
    caption: 'اختيار المفسر والمنهج'
  },
  {
    file: 'Screenshot (4).webp',
    width: 244,
    height: 128,
    name: 'نتيجة التفسير الكامل للحلم المرتبطة بظروف الرائي في تطبيق ابن سيرين للأندرويد',
    caption: 'التفسير المركّب الكامل للحلم'
  },
  {
    file: 'Screenshot (5).webp',
    width: 244,
    height: 242,
    name: 'الإدخال الصوتي وسجل التفسيرات والمفضلة في تطبيق ابن سيرين للأندرويد',
    caption: 'الإدخال الصوتي والسجل والمفضلة'
  }
];

export function appScreenshotUrl(index: number): string {
  const shot = APP_SCREENSHOTS[index];
  return `${SITE_CONFIG.baseUrl}/${encodeURIComponent(shot.file)}`;
}

function resolveCanonicalSlugForDream(dream: DreamTopic): string {
  if (!dream.isDictionary) return dream.slug;
  const featuredSlugs = new Set(DREAMS_DATABASE.map(d => d.slug));
  const viaEditorial = EDITORIAL_RESOLUTION[dream.arabicSlug];
  if (viaEditorial && viaEditorial !== dream.slug && featuredSlugs.has(viaEditorial)) {
    return viaEditorial;
  }
  const entry = findDictionaryBySlug(dream.slug);
  if (entry && entry.canonicalSlug && featuredSlugs.has(entry.canonicalSlug)) {
    return entry.canonicalSlug;
  }
  return dream.slug;
}

export function getHomePageSeo(queryDream?: string): PageSeoMetadata {
  const hasAiQuery = !!queryDream;
  const canonicalUrl = SITE_CONFIG.baseUrl;

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ابن سيرين لتفسير الأحلام',
    url: SITE_CONFIG.baseUrl,
    description: SITE_CONFIG.defaultDescription,
    inLanguage: 'ar',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_CONFIG.baseUrl}/dictionary?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'تطبيق ابن سيرين لتفسير الأحلام',
    operatingSystem: 'Web, Android, iOS',
    applicationCategory: 'LifestyleApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ابن سيرين لتفسير الأحلام',
    url: SITE_CONFIG.baseUrl,
    logo: `${SITE_CONFIG.baseUrl}/crescent-moon.svg`
  };

  return {
    title: hasAiQuery
      ? `تفسير حلم: ${queryDream.slice(0, 60)} بالذكاء الاصطناعي | ابن سيرين`
      : 'ابن سيرين | تفسير الأحلام والرؤى بالذكاء الاصطناعي وقاموس الرموز الشامل',
    description: SITE_CONFIG.defaultDescription,
    canonicalUrl,
    keywords: [
      'تفسير الاحلام',
      'ابن سيرين',
      'النابلسي',
      'تفسير حلم',
      'مفسر احلام بالذكاء الاصطناعي',
      'تفسير الاحلام مجانا',
      'قاموس تفسير الاحلام',
      'تفسير الرؤى والمنامات'
    ],
    ogType: 'website',
    ogImage: SITE_CONFIG.ogImage,
    jsonLdSchemas: [websiteSchema, softwareSchema, orgSchema],
    noindex: hasAiQuery,
    robots: hasAiQuery ? 'noindex, nofollow' : 'index, follow'
  };
}

export function getDictionaryPageSeo(): PageSeoMetadata {
  const canonicalUrl = cleanCanonicalUrl('/dictionary');

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'الرئيسية',
        item: SITE_CONFIG.baseUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'قاموس تفسير الأحلام',
        item: canonicalUrl
      }
    ]
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'قاموس تفسير الأحلام والرؤى الشامل بالحروف والأقسام',
    url: canonicalUrl,
    description: 'دليل موسوعي مفهرس لجميع رموز الأحلام بالحروف الأبجدية والأقسام مستند إلى مصادر التراث التفسيري.',
    inLanguage: 'ar'
  };

  return {
    title: 'قاموس تفسير الأحلام الشامل بالحروف والأقسام | قاموس الرؤى التراثي',
    description: 'تصفح موسوعة تفسير الأحلام الشاملة مفهرسة بالحروف الأبجدية والتصنيفات. دلالات الثعبان، الذهب، الموت، تساقط الأسنان، البحر، والمزيد مستندة إلى مصادر تراثية.',
    canonicalUrl,
    keywords: [
      'قاموس تفسير الاحلام',
      'تفسير الاحلام بالحروف',
      'معجم تفسير الاحلام',
      'ابن سيرين بالحروف الابجدية',
      'موسوعة الرؤى والاحلام'
    ],
    ogType: 'website',
    ogImage: SITE_CONFIG.ogImage,
    jsonLdSchemas: [breadcrumbSchema, collectionSchema]
  };
}

export function getDreamTopicSeo(dream: DreamTopic): PageSeoMetadata {
  const canonicalSlug = resolveCanonicalSlugForDream(dream);
  const canonicalUrl = cleanCanonicalUrl(`/dream/${canonicalSlug}`);
  const isDuplicate = canonicalSlug !== dream.slug;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'الرئيسية',
        item: SITE_CONFIG.baseUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'قاموس الأحلام',
        item: cleanCanonicalUrl('/dictionary')
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: dream.category,
        item: cleanCanonicalUrl(`/category/${dream.categorySlug}`)
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: dream.name,
        item: canonicalUrl
      }
    ]
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': dream.isDictionary ? 'Article' : 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl
    },
    headline: dream.title,
    description: dream.metaDescription,
    image: SITE_CONFIG.ogImage,
    author: {
      '@type': 'Organization',
      name: 'هيئة تحرير قاموس ابن سيرين للرؤى',
      url: SITE_CONFIG.baseUrl
    },
    publisher: {
      '@type': 'Organization',
      name: 'قاموس ابن سيرين',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_CONFIG.baseUrl}/crescent-moon.svg`
      }
    },
    datePublished: `${dream.datePublished}T08:00:00+03:00`,
    dateModified: `${dream.dateModified}T12:00:00+03:00`,
    inLanguage: 'ar'
  };

  const definedTermSchema = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    name: dream.name,
    description: dream.summary,
    inDefinedTermSet: cleanCanonicalUrl('/dictionary')
  };

  const faqSchema = dream.faqs && dream.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: dream.faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  } : null;

  const schemas: object[] = [breadcrumbSchema];
  if (!isDuplicate) {
    schemas.push(articleSchema, definedTermSchema);
    if (faqSchema) schemas.push(faqSchema);
  }

  return {
    title: `${dream.title}${dream.isDictionary ? '' : ' | ابن سيرين'}`,
    description: dream.metaDescription,
    canonicalUrl,
    keywords: dream.keywords,
    ogType: 'article',
    ogImage: SITE_CONFIG.ogImage,
    publishedTime: `${dream.datePublished}T08:00:00+03:00`,
    modifiedTime: `${dream.dateModified}T12:00:00+03:00`,
    jsonLdSchemas: schemas,
    noindex: isDuplicate,
    robots: isDuplicate ? 'noindex, follow' : 'index, follow'
  };
}

export function getLetterPageSeo(letter: string): PageSeoMetadata {
  const canonicalUrl = cleanCanonicalUrl(`/letter/${encodeURIComponent(letter)}`);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'الرئيسية',
        item: SITE_CONFIG.baseUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'قاموس الأحلام',
        item: cleanCanonicalUrl('/dictionary')
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `حرف ${letter}`,
        item: canonicalUrl
      }
    ]
  };

  return {
    title: `تفسير الأحلام بحرف (${letter}) | فهرس قاموس الرؤى التراثي`,
    description: `فهرس شامل لجميع رموز الأحلام التي تبدأ بحرف (${letter}) مستند إلى مصادر التراث التفسيري للعزباء والمتزوجة والحامل والرجل.`,
    canonicalUrl,
    keywords: [`تفسير الاحلام بحرف ${letter}`, `احلام تبدأ بحرف ${letter}`, `معجم الاحلام حرف ${letter}`, 'قاموس الرؤى'],
    ogType: 'website',
    ogImage: SITE_CONFIG.ogImage,
    jsonLdSchemas: [breadcrumbSchema]
  };
}

export function getCategoryPageSeo(categorySlug: string): PageSeoMetadata {
  const category = DREAM_CATEGORIES.find(c => c.slug === categorySlug);
  const categoryName = category ? category.name : 'أقسام الأحلام';
  const canonicalUrl = cleanCanonicalUrl(`/category/${categorySlug}`);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'الرئيسية',
        item: SITE_CONFIG.baseUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'قاموس الأحلام',
        item: cleanCanonicalUrl('/dictionary')
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: categoryName,
        item: canonicalUrl
      }
    ]
  };

  return {
    title: `تفسير أحلام ${categoryName} في المنام | قاموس الرؤى التراثي`,
    description: `موسوعة تفسير أحلام ${categoryName} مستندة إلى مصادر التراث التفسيري بالتفصيل الشامل للعزباء والمتزوجة والحامل والرجل والمطلقة.`,
    canonicalUrl,
    keywords: [`تفسير أحلام ${categoryName}`, 'تفسير الرؤى', 'مصادر تراثية لتفسير الأحلام'],
    ogType: 'website',
    ogImage: SITE_CONFIG.ogImage,
    jsonLdSchemas: [breadcrumbSchema]
  };
}

export function getPrivacyPolicySeo(): PageSeoMetadata {
  const canonicalUrl = cleanCanonicalUrl('/privacy');

  return {
    title: 'سياسة الخصوصية وحماية البيانات | قاموس ابن سيرين للرؤى',
    description: 'سياسة الخصوصية وسرية البيانات لموقع ابن سيرين لتفسير الأحلام. التزام كامل بحماية سرية الرؤى والبيانات الشخصية.',
    canonicalUrl,
    keywords: ['سياسة الخصوصية ابن سيرين', 'حماية بيانات الاحلام'],
    ogType: 'website',
    ogImage: SITE_CONFIG.ogImage,
    jsonLdSchemas: []
  };
}

export function getAppPageSeo(): PageSeoMetadata {
  const canonicalUrl = cleanCanonicalUrl('/app');
  const screenshots = APP_SCREENSHOTS.map((s, i) => ({
    '@type': 'ImageObject',
    url: appScreenshotUrl(i),
    name: s.name,
    caption: s.caption
  }));

  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'تفسير الأحلام لابن سيرين و AI',
    alternateName: 'تطبيق ابن سيرين لتفسير الأحلام والذكاء الاصطناعي',
    url: canonicalUrl,
    downloadUrl: GOOGLE_PLAY_APP_URL,
    installUrl: GOOGLE_PLAY_APP_URL,
    image: `${SITE_CONFIG.baseUrl}/ibn-sirin.png`,
    screenshot: screenshots,
    operatingSystem: 'Android',
    applicationCategory: 'LifestyleApplication',
    contentRating: 'مناسب لأعمار 3 أعوام وما فوق (Everyone)',
    isAccessibleForFree: true,
    inLanguage: 'ar',
    description: 'تفسير الحلم الكامل بالذكاء الاصطناعي بأسلوب ثلاثة مفسرين (ابن سيرين والنابلسي وابن شاهين الظاهري) مع البحث في المكتبة التراثية الشاملة بدون إنترنت، والإدخال الصوتي، وسجل التفسيرات والمفضلة. تطبيق مجاني من متجر Google Play بتقييم 4.9★ وأكثر من 1000 عملية تنزيل.',
    dateModified: '2026-09-19',
    releaseNotes: 'تحسين معجم البحث في تفسير الأحلام لابن سيرين وابن شاهين والنابلسي وإضافة تفسيرات جديدة، وتحديث محرك التفسير بالذكاء الاصطناعي.',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '31',
      bestRating: '5',
      worstRating: '1'
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      category: 'Free'
    },
    publisher: {
      '@type': 'Organization',
      name: 'AhmeDaher Soft',
      url: canonicalUrl
    },
    author: {
      '@type': 'Organization',
      name: 'AhmeDaher Soft',
      email: 'ibn-sirin@mshru3.com'
    },
    sameAs: [GOOGLE_PLAY_APP_URL, SITE_CONFIG.baseUrl]
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': canonicalUrl,
    url: canonicalUrl,
    name: 'تحميل تطبيق تفسير الأحلام لابن سيرين والذكاء الاصطناعي للأندرويد',
    description: 'حمّل تطبيق تفسير الأحلام لابن سيرين و AI مجاناً من Google Play لتفسير حلمك كاملاً بالذكاء الاصطناعي مع اختيار المفسر والمنهج والمكتبة التراثية والإدخال الصوتي والسجل والمفضلة.',
    inLanguage: 'ar',
    isPartOf: {
      '@type': 'WebSite',
      '@id': SITE_CONFIG.baseUrl,
      name: SITE_CONFIG.siteName,
      url: SITE_CONFIG.baseUrl
    },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: appScreenshotUrl(0)
    },
    image: screenshots,
    about: {
      '@type': 'SoftwareApplication',
      name: 'تفسير الأحلام لابن سيرين و AI'
    },
    dateModified: '2026-09-19'
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'الرئيسية',
        item: SITE_CONFIG.baseUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'تحميل التطبيق',
        item: canonicalUrl
      }
    ]
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'ما الفرق بين موقع ابن سيرين وتطبيق الأندرويد؟',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'الموقع موسوعة مفتوحة تشرح دلالة الرمز الواحد بالتفصيل (مثل الثعبان، الذهب، البحر) للقراءة والبحث. أما التطبيق فيفسّر الحلم كاملاً: تكتب رؤياك بكل رموزها فيجمعها الذكاء الاصطناعي في تفسير متكامل مرتبط بظروفك، مع الإدخال الصوتي والسجل والمفضلة.'
        }
      },
      {
        '@type': 'Question',
        name: 'هل تحميل تطبيق تفسير الأحلام لابن سيرين مجاني؟',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'نعم، التطبيق متاح للتحميل مجاناً من متجر Google Play ويشمل ميزات التفسير بالذكاء الاصطناعي بأسلوب ابن سيرين والنابلسي وابن شاهين الظاهري والبحث في المكتبة التراثية بدون إنترنت.'
        }
      },
      {
        '@type': 'Question',
        name: 'من أين أنزّل التطبيق الرسمي لتفسير الأحلام؟',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'رابط التحميل الرسمي الموثق هو عبر متجر Google Play بعنوان "تفسير الأحلام لابن سيرين و AI". ننصح دائماً بالتحميل من المتجر الرسمي للحصول على آخر إصدار.'
        }
      },
      {
        '@type': 'Question',
        name: 'هل يعتمد التفسير في التطبيق على مصادر تراثية موثقة؟',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'نعم، يُبنى التفسير على المادة التراثية المنقولة عن أهل التعبير (ابن سيرين والنابلسي وابن شاهين الظاهري وغيرهم مما تتوفر له مادة)، ويُذكر بكل تفسير أنه اجتهاد آلي لا يُنسب مباشرة لأحد المفسرين.'
        }
      }
    ]
  };

  return {
    title: 'تحميل تطبيق تفسير الأحلام لابن سيرين والذكاء الاصطناعي للأندرويد مجاناً',
    description: 'حمّل تطبيق تفسير الأحلام لابن سيرين و AI مجاناً من Google Play (تقييم 4.9★ وأكثر من 1000 تنزيل). تفسير الحلم الكامل بالذكاء الاصطناعي بأسلوب ابن سيرين والنابلسي وابن شاهين، مع المكتبة التراثية بدون إنترنت والإدخال الصوتي.',
    canonicalUrl,
    keywords: [
      'تحميل تطبيق تفسير الاحلام',
      'تطبيق ابن سيرين للاندرويد',
      'تفسير الاحلام بالذكاء الاصطناعي تطبيق',
      'تفسير الحلم الكامل',
      'تطبيق ابن سيرين النابلسي',
      'تفسير الاحلام لابن سيرين و AI',
      'تنزيل تطبيق ابن سيرين',
      'تطبيق تفسير الاحلام مجانا'
    ],
    ogType: 'website',
    ogImage: appScreenshotUrl(0),
    ogImageWidth: 244,
    ogImageHeight: 128,
    jsonLdSchemas: [softwareSchema, webPageSchema, breadcrumbSchema, faqSchema]
  };
}
